import { FC, useEffect, useRef, useState, useContext } from 'react';
import clsx from 'clsx';
import { setCookie, getCookies } from '../../utils/cookieUtil';
import { PageParamsContext } from '../../../../modules/context/PageContext';
import { Constants, LOYALTY_ENABLED_ZONES } from '../../utils/constant';
import {
  analyticsTrackingModalIdentifier,
  analyticsTrackingUniqueDescription,
  getInternetBarTrackingIdentifier,
} from '../../../../modules/utils/constants';
import {
  submitDynamicForm,
  getEnrolledUserFlag,
  connectBtnTrigger,
  getLoyaltyEnabledForConferenceZone,
} from '../../utils/handler';
import { LoginModalType } from '../../InternetBar.types';
import { InternetBarLoginModalProps } from './InternetBarModal.types';
import { Button, InputTextField, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { ButtonTypeVariation } from '../../../../modules/utils/enums/enums';

export const InternetBarLoginModal: FC<InternetBarLoginModalProps> = (props: InternetBarLoginModalProps) => {
  const { preProcessorResponse } = useContext(PageParamsContext);
  const { loginModal, loginModalType, internetBarDataValues, currentStateMapping } = props;
  const login = props?.login as LoginModalType;
  const [accessHref, setAccessHref] = useState<string>('');
  const [reqSMSHref, setReqSMSHref] = useState<string>('');
  const [isUserEnrolled, setIsUserEnrolled] = useState(false);
  const inputRef = useRef(null);
  const [isPrimaryButtonLoading, setIsPrimaryButtonLoading] = useState<boolean>(false);
  const [isSecondaryButtonLoading, setIsSecondaryButtonLoading] = useState<boolean>(false);

  const {
    enableLoyalty = false,
    enableLoyaltyConference = false,
    enrollmentSourceCodeMapping,
    propertyInfo,
    state = '',
  } = preProcessorResponse || {};
  const isNoLoginInterfaceModal = currentStateMapping?.noLoginInterfaceModal ?? false;
  const isMobileAndAboveViewPort = useCheckBreakpoint('viewportS'); // check if viewport is Mobile and above
  const [loyaltyForConferenceZone, setLoyaltyForConferenceZone] = useState<boolean>(false);

  const isLoyaltyEnabledForConference = getLoyaltyEnabledForConferenceZone(
    enableLoyaltyConference ? enrollmentSourceCodeMapping?.loyalty : enrollmentSourceCodeMapping?.nonloyalty,
    propertyInfo?.data?.basicInformation?.globalDivision,
    propertyInfo?.data?.contactInformation?.address?.country?.code
  );

  const {
    MARSHA = '',
    postEndpoint = '',
    U_URLEndpoint = '',
    LR = '',
    Z = '',
    ACTION = '',
    DOMAIN = '',
    MS,
    APS,
    zoneMapping = '',
    usageAttr = '',
    USAGE = '',
    DURATION = '',
    ppv5Enrollment = false,
    loyaltyEnrollmentSourceCode,
  } = internetBarDataValues || {};

  const propertyTier = zoneMapping?.['GUEST'];
  const isUpgrade =
    ACTION === 'CONNECTED' && Z === 'GUEST' && U_URLEndpoint !== '' && (propertyTier === 'FP' || propertyTier === 'PP')
      ? true
      : false;

  const [isLoyaltyEnabled] = useState(
    (enableLoyalty || ppv5Enrollment) && LOYALTY_ENABLED_ZONES?.includes((Z || '')?.toUpperCase())
  );

  const [tierInfo] = useState(zoneMapping[Z ?? ''] ?? undefined);

  const [loginModalError, setLoginModalError] = useState<boolean>(loginModalType === 'LOGERR' ? true : false);

  const [loginDetails, setLoginDetails] = useState({
    roomNumber: '',
    lastName: '',
  });

  const [loginError, setLoginError] = useState({
    roomNumberError: false,
    lastNameError: false,
    showRoomNumberError: false,
    showLastNameError: false,
  });

  const getLoginModalInformation = () => {
    // saving cookie for user login data
    const userInfoCookie = getCookies('PPv5UserInfo');
    if (userInfoCookie) {
      const decodedText = window.atob(userInfoCookie);
      const textDecoderInstance = new TextDecoder('utf-8');
      const cookieDecoded = textDecoderInstance.decode(
        new Uint8Array([...decodedText].map(char => char.charCodeAt(0)))
      );
      const userInfoDetails = cookieDecoded.split('|');

      if (userInfoDetails.length === 3 && internetBarDataValues?.['MARSHA'] === userInfoDetails[0]) {
        setLoginDetails({
          lastName: userInfoDetails[1],
          roomNumber: userInfoDetails[2],
        });
      }
    }
  };

  const loginBtnTrigger = (): void => {
    const lastNameValue = loginDetails?.lastName;
    const roomNumberValue = loginDetails?.roomNumber;

    const endpoint = isUpgrade ? U_URLEndpoint : postEndpoint;
    const action = isUpgrade ? 'UPGRADE' : ACTION;

    if (lastNameValue && roomNumberValue && (postEndpoint || U_URLEndpoint)) {
      try {
        const cookieKey = 'PPv5UserInfo';
        const textEncoderInstance = new TextEncoder();
        const encodedText = textEncoderInstance.encode(`${MARSHA}|${lastNameValue}|${roomNumberValue}`);
        const cookieValue = window.btoa(String.fromCharCode.apply(null, Array.from(encodedText)));
        setCookie(cookieKey, cookieValue);
        const obj = {
          MARSHA: MARSHA ? MARSHA : undefined,
          LR: !currentStateMapping?.showUpgradeModal ? LR : undefined,
          Z: !currentStateMapping?.showUpgradeModal ? Z : undefined,
          ACTION: action ? action : undefined,
          DOMAIN: DOMAIN ? DOMAIN : undefined,
          MS: MS || MS === '' ? MS : undefined,
          APS: APS,
          ROOM_NUMBER: roomNumberValue.trim(),
          LAST_NAME: lastNameValue.trim(),
          TIERS: zoneMapping[Z ?? ''] ?? undefined,
        };
        const body = JSON.stringify(obj);
        submitDynamicForm(body, endpoint);
      } catch (error) {
        // ignore
      }
    }
  };

  const freeConnectBtnTrigger = (): void => {
    setIsSecondaryButtonLoading(true);
    if (postEndpoint) {
      try {
        const aemUSAGE = usageAttr === Constants.usage.ELITE ? Constants.usage.ELITE : Constants.usage.FREE;
        const usage = USAGE ? USAGE : aemUSAGE;
        const sp =
          usageAttr === Constants.usage.ELITE &&
          (zoneMapping[Z ?? 'GUEST'] === 'FP' || zoneMapping[Z ?? 'GUEST'] === 'PP')
            ? Constants.plan.PLAN2
            : Constants.plan.PLAN1;
        const obj = {
          MARSHA: MARSHA ? MARSHA : undefined,
          LR: LR,
          Z: Z,
          ACTION: 'CONNECTED',
          DOMAIN: DOMAIN ? DOMAIN : undefined,
          MS: MS || MS === '' ? MS : undefined,
          APS: APS,
          USAGE: usage,
          SP: sp,
          DURATION: DURATION ? DURATION : undefined,
          TIERS: zoneMapping[Z ?? ''] ?? undefined,
        };
        const body = JSON.stringify(obj);
        submitDynamicForm(body, postEndpoint);
      } catch (error) {
        // ignore
      }
    }
  };

  const getPrimaryButtonCta = (): string => {
    let primaryBtnCTA: string | undefined = '';

    if (enableLoyalty || (loyaltyForConferenceZone && enableLoyaltyConference)) {
      if (propertyTier?.toUpperCase() === 'SF' || tierInfo?.toUpperCase() === 'SF') {
        primaryBtnCTA = login?.loyaltyContinueAsMemberCta;
      } else {
        primaryBtnCTA = login?.loginContinueButtonLabel;
      }
    } else if (isUserEnrolled && ppv5Enrollment) {
      if (
        propertyTier?.toUpperCase() === 'SF' ||
        tierInfo?.toUpperCase() === 'SF' ||
        propertyTier?.toUpperCase() === 'SP' ||
        tierInfo?.toUpperCase() === 'SP'
      ) {
        primaryBtnCTA = login?.loyaltyContinueAsMemberCta;
      } else {
        primaryBtnCTA = login?.loginContinueButtonLabel;
      }
    } else {
      primaryBtnCTA = login?.loginButtonLabel;
    }
    return primaryBtnCTA || '';
  };

  const getSecondaryButtonCta = (): string => {
    let secondaryBtnCTA: string | undefined = '';
    if (
      (enableLoyalty || (loyaltyForConferenceZone && enableLoyaltyConference)) &&
      (propertyTier?.toUpperCase() === 'SF' ||
        tierInfo?.toUpperCase() === 'SF' ||
        propertyTier?.toUpperCase() === 'FP' ||
        tierInfo?.toUpperCase() === 'FP')
    ) {
      secondaryBtnCTA = login?.loyaltyConnectAsGuestCta;
    } else if (
      isUserEnrolled &&
      ppv5Enrollment &&
      (propertyTier?.toUpperCase() === 'SF' ||
        tierInfo?.toUpperCase() === 'SF' ||
        propertyTier?.toUpperCase() === 'FP' ||
        tierInfo?.toUpperCase() === 'FP' ||
        propertyTier?.toUpperCase() === 'SP' ||
        tierInfo?.toUpperCase() === 'SP')
    ) {
      secondaryBtnCTA = login?.loyaltyConnectAsGuestCta;
    } else {
      secondaryBtnCTA = login?.connectNowLabel;
    }

    return secondaryBtnCTA ?? '';
  };

  useEffect(() => {
    setLoyaltyForConferenceZone(isLoyaltyEnabledForConference);
  }, [isLoyaltyEnabledForConference]);

  useEffect(() => {
    const handleQueryParamUpdate = (paramName: string, paramValue: string) => {
      const accessParams = new URLSearchParams(window.location.search);
      accessParams.set(paramName, paramValue);
      return `?${accessParams.toString()}`;
    };

    const access = handleQueryParamUpdate('MV', 'ACCESS');
    setAccessHref(access);
    const reqSMS = handleQueryParamUpdate('MV', 'REQSMS');
    setReqSMSHref(reqSMS);

    const enrolled = getEnrolledUserFlag(loyaltyEnrollmentSourceCode);
    setIsUserEnrolled(enrolled);
  }, [loyaltyEnrollmentSourceCode]);

  useEffect(() => {
    if (loginModalType === 'LOGERR') {
      setLoginModalError(true);
      setLoginError({
        ...loginError,
        roomNumberError: true,
        lastNameError: true,
        showLastNameError: true,
        showRoomNumberError: true,
      });
    }
    getLoginModalInformation();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      isMobileAndAboveViewPort && inputRef && inputRef?.current && (inputRef?.current as HTMLInputElement)?.focus();
    }, 20);

    return () => clearTimeout(timeout);
  }, [isMobileAndAboveViewPort]);

  return (
    <>
      {/* 1. Internet Bar Login Modal Default Configuration */}
      {loginModal?.default && (
        <div className="body-container d-flex flex-column" data-testid="internetbar_login-modal">
          {/* 1.1 Internet Bar Login Modal Error */}
          {loginModal?.errorMessage && loginModalError && (
            <div className="d-flex pb-2">
              <span
                className={clsx(
                  `col-12 px-3 py-3 mb-4 internetbarmodal__error ${loginModal?.connectNowLabel ? 't-font-s' : ''}`
                )}
              >
                {login?.errorMessage}
              </span>
            </div>
          )}

          {isUserEnrolled && ppv5Enrollment && (
            <>
              {login?.loyaltyLoginWelcomeText && <h6 className="t-subtitle-m">{login?.loyaltyLoginWelcomeText}</h6>}
              {login?.loyaltyLoginDescription && <p className="t-font-xs pb-3">{login?.loyaltyLoginDescription}</p>}
            </>
          )}

          {enableLoyalty || (loyaltyForConferenceZone && enableLoyaltyConference) ? (
            <span className="t-subtitle-m pb-2">{login?.loyaltyGuestAtHotelDescription}</span>
          ) : isUserEnrolled && ppv5Enrollment ? null : (
            <span className="t-subtitle-m pb-2">{login?.guestLoginLabel}</span>
          )}

          {!(isUserEnrolled && ppv5Enrollment) && <span className="t-font-xs pb-4">{login?.requiredLabel}</span>}
          <div className="m-form-group pb-5 mb-3">
            <InputTextField
              type="text"
              label={login?.roomNumberLabel}
              placeHolderText={login?.roomNumberPlaceHolder}
              classNameForLineType="m-form-group mb-0"
              inputTextFieldClassName={`text-field pb-2 t-font-m px-0 ${
                loginError?.roomNumberError || loginModalError ? 'is-error' : ''
              }`}
              custom_click_track_value={`${getInternetBarTrackingIdentifier(
                state,
                analyticsTrackingModalIdentifier.loginModal,
                zoneMapping?.[Z ?? 'GUEST']
              )}|${analyticsTrackingUniqueDescription.loginModalRoomNumber}|internal`}
              showIcon={false}
              getInputProps={() => ({
                ref: inputRef,
              })}
              variation="line-type"
              showUnderline={true}
              hideFontClass={true}
              togglePlaceholder={loginDetails?.roomNumber?.length > 0 ? false : true}
              assignInputValue={true}
              onBlur={() => {
                if (loginDetails?.roomNumber?.length === 0 && loginError?.showRoomNumberError) {
                  setLoginError({ ...loginError, roomNumberError: true });
                } else {
                  setLoginError({ ...loginError, roomNumberError: false });
                }
              }}
              getInputValue={value => {
                setLoginDetails({ ...loginDetails, roomNumber: value });
                setLoginError({ ...loginError, roomNumberError: false });
                if (value?.length > 0) {
                  setLoginError({ ...loginError, showRoomNumberError: true });
                  if (loginDetails?.lastName?.length > 0) {
                    setLoginModalError(false);
                  }
                }
              }}
              onFocus={() => {
                setIsPrimaryButtonLoading(false);
                setIsSecondaryButtonLoading(false);
                setLoginError({ ...loginError, roomNumberError: false });
              }}
              showErrorMessage={loginError?.roomNumberError || loginModalError}
              inputValue={loginDetails?.roomNumber}
            />
          </div>
          <div className="m-form-group pb-5 mb-3">
            <InputTextField
              type="text"
              label={login?.lastNameLabel}
              placeHolderText={login?.lastNamePlaceHolder}
              classNameForLineType="m-form-group mb-0"
              inputTextFieldClassName={`text-field pb-2 t-font-m px-0 ${
                loginError?.lastNameError || loginModalError ? 'is-error' : ''
              }`}
              togglePlaceholder={loginDetails?.lastName?.length > 0 ? false : true}
              showIcon={false}
              variation="line-type"
              showUnderline={true}
              hideFontClass={true}
              assignInputValue={true}
              custom_click_track_value={`${getInternetBarTrackingIdentifier(
                state,
                analyticsTrackingModalIdentifier.loginModal,
                zoneMapping?.[Z ?? 'GUEST']
              )}|${analyticsTrackingUniqueDescription.loginModalLastName}|internal`}
              onBlur={() => {
                if (loginDetails?.lastName?.length === 0 && loginError?.showLastNameError) {
                  setLoginError({ ...loginError, lastNameError: true });
                } else {
                  setLoginError({ ...loginError, lastNameError: false });
                }
              }}
              onFocus={() => {
                setIsPrimaryButtonLoading(false);
                setIsSecondaryButtonLoading(false);
                setLoginError({ ...loginError, lastNameError: false });
              }}
              getInputValue={value => {
                setLoginDetails({ ...loginDetails, lastName: value });
                setLoginError({ ...loginError, lastNameError: false });
                if (value?.length > 0) {
                  setLoginError({ ...loginError, showLastNameError: true });
                  if (loginDetails?.roomNumber?.length > 0) {
                    setLoginModalError(false);
                  }
                }
              }}
              showErrorMessage={loginError?.lastNameError || loginModalError}
              inputValue={loginDetails?.lastName}
            />
          </div>
          <Button
            isLink={false}
            type={ButtonTypeVariation.Button}
            className={clsx(
              `m-button-m m-button-primary t-font-m`,
              loginDetails?.lastName?.trim()?.length === 0 ||
                loginDetails?.roomNumber?.trim()?.length === 0 ||
                isPrimaryButtonLoading
                ? 'disabled'
                : ''
            )}
            custom_click_track_value={`${getInternetBarTrackingIdentifier(
              state,
              analyticsTrackingModalIdentifier.loginModal,
              zoneMapping?.[Z ?? 'GUEST']
            )}|${getPrimaryButtonCta()} Button|external`}
            isDisabled={
              loginDetails?.lastName?.trim()?.length === 0 ||
              loginDetails?.roomNumber?.trim()?.length === 0 ||
              isPrimaryButtonLoading
            }
            children={
              isPrimaryButtonLoading ? (
                <div className="m-spinner-s" data-testid="activate-spinner"></div>
              ) : (
                getPrimaryButtonCta()
              )
            }
            callback={
              !LR &&
              (propertyTier?.toUpperCase() === 'SF' || tierInfo?.toUpperCase() === 'SF') &&
              (isLoyaltyEnabled || (isUserEnrolled && ppv5Enrollment))
                ? () => {
                    setIsPrimaryButtonLoading(true);
                    connectBtnTrigger(
                      (propertyTier?.toUpperCase() === 'SF' || tierInfo?.toUpperCase() === 'SF') &&
                        (isLoyaltyEnabled || (isUserEnrolled && ppv5Enrollment))
                        ? true
                        : isNoLoginInterfaceModal,
                      internetBarDataValues
                    );
                  }
                : () => {
                    setIsPrimaryButtonLoading(true);
                    loginBtnTrigger();
                  }
            }
          />
        </div>
      )}
      <div className="d-flex flex-column">
        {/* 2. Internet Bar Login Modal Separator Configuration  */}
        {loginModal?.separatorLabel && (
          <span className="py-2 my-1 d-flex flex-row align-items-center justify-content-center">
            <span className="w-100 internetbarmodal__seperator"></span>
            <span className="px-2 mx-1 t-font-xs">{login?.separatorLabel}</span>
            <span className="w-100 internetbarmodal__seperator"></span>
          </span>
        )}
        {/* 2.1. Internet Bar Login Modal Connect Button Configuration  */}
        {loginModal?.connectNowLabel && !(isUserEnrolled && ppv5Enrollment && LR) && (
          <Button
            isLink={false}
            type={ButtonTypeVariation.Button}
            className={`m-button-m m-button-secondary ${isSecondaryButtonLoading ? 'disabled' : ''} t-font-m`}
            custom_click_track_value={`${getInternetBarTrackingIdentifier(
              state,
              analyticsTrackingModalIdentifier.loginModal,
              zoneMapping?.[Z ?? 'GUEST']
            )}|${getSecondaryButtonCta()} Button|external`}
            isDisabled={isSecondaryButtonLoading}
            children={
              isSecondaryButtonLoading ? (
                <div className="m-spinner-s" data-testid="activate-spinner"></div>
              ) : (
                getSecondaryButtonCta()
              )
            }
            callback={freeConnectBtnTrigger}
          />
        )}
        {/* 3. Internet Bar Login Modal Access Code Configuration  */}
        {loginModal?.userAccessCodeLabel && (
          <div className="d-flex flex-column mx-auto text-center">
            <a
              href={accessHref}
              className="t-font-s internetbarmodal__link custom_click_track"
              data-custom_click_track_value={`${getInternetBarTrackingIdentifier(
                state,
                analyticsTrackingModalIdentifier.loginModal,
                zoneMapping?.[Z ?? 'GUEST']
              )}|${analyticsTrackingUniqueDescription.loginModalUserAccessCodeLabel}|internal`}
            >
              {login?.userAccessCodeLabel}
            </a>
          </div>
        )}
        {/* 4. Internet Bar Login Modal Access Code via SMS Configuration  */}
        {/* @todo Update this with Access Code via SMS  */}
        {loginModal?.requestAccessCodeLabel && (
          <div className="d-flex flex-column mx-auto text-center">
            <a
              href={reqSMSHref}
              className={clsx(
                `t-font-s internetbarmodal__link custom_click_track ${
                  currentStateMapping?.showAccessModal ? 'mt-2 pt-1' : ''
                }`
              )}
              data-custom_click_track_value={`${getInternetBarTrackingIdentifier(
                state,
                analyticsTrackingModalIdentifier.loginModal,
                zoneMapping?.[Z ?? 'GUEST']
              )}|${analyticsTrackingUniqueDescription.loginModalRequestAccessCodeLabel}|internal`}
            >
              {login?.requestAccessCodeLabel}
            </a>
          </div>
        )}
      </div>
    </>
  );
};
